<template>
  <b-form @submit.prevent="onSubmit" @keydown="form.errors.clear($event.target.id)">
    <div class="row">
      <div class="col">
        <b-card>
          <template #header>
            <b>Reminder</b>
          </template>

          <div
            v-if="creating"
            class="form-group row"
            :class="{ 'invalid-wrapper': form.errors.has('job_id') }"
          >
            <div class="col-3 col-form-label text-right">Related to Job?</div>
            <div class="col-8">
              <core-switch
                v-model="form.related_to_job"
                variant="3d"
                color="primary"
                on="Y"
                off="N"
                @change="form.errors.clear('job_id')"
              />

              <small v-if="form.errors.has('job_id')" class="invalid-feedback">
                Can only create job related reminder from Dispatch Board.
              </small>
            </div>
          </div>

          <div class="form-group row" :class="{ 'invalid-wrapper': form.errors.has('important') }">
            <div class="col-3 col-form-label text-right">Important?</div>
            <div class="col-8">
              <core-switch
                v-model="form.important"
                variant="3d"
                color="primary"
                on="Y"
                off="N"
                @change="form.errors.clear('important')"
              />

              <small
                v-if="form.errors.has('important')"
                class="invalid-feedback"
                v-text="form.errors.get('important')"
              ></small>
            </div>
          </div>

          <form-user-select
            v-model="form.user"
            name="user_id"
            user-role="superadmin,admin"
            placeholder="Search user..."
            bypass-filter="brandMentor"
            @change="updateAssignee"
          >
            Assigned To
          </form-user-select>
          <form-select v-model="form.action" name="action" :options="actions" />
          <form-date v-model="form.due_date" name="due_date" value-format="yyyy-MM-dd" />
          <form-textarea v-model="form.notes" name="notes" />
        </b-card>
      </div>

      <div class="col-4">
        <b-card>
          <template #header>
            <strong>Job</strong>
            Info
          </template>

          <div>
            <b>Job </b>
            <template v-if="form.related_to_job && currentJobId">
              <job-object :job-number="currentJobId" :entity-id="currentJobId" :open-new="true" />
            </template>
            <template v-else> Not related to a Job </template>
          </div>

          <template v-if="form.related_to_job && currentJobId">
            <div class="mt-2">
              <b>Job Contact </b>
              <template v-if="$lodash.has(relatedJobData, 'job_contacts[0].name')">
                <customer-object
                  :name="$lodash.get(relatedJobData, 'job_contacts[0].name')"
                  :entity-id="$lodash.get(relatedJobData, 'job_contacts[0].id')"
                  :open-new="true"
                />
              </template>
              <template v-else> N/A </template>
            </div>

            <div class="mt-2">
              <b>Billing Contact </b>
              <template v-if="$lodash.has(relatedJobData, 'billing_contacts[0].name')">
                <customer-object
                  :name="$lodash.get(relatedJobData, 'billing_contacts[0].name')"
                  :entity-id="$lodash.get(relatedJobData, 'billing_contacts[0].id')"
                  :open-new="true"
                />
              </template>
              <template v-else-if="$lodash.has(relatedJobData, 'job_contacts[0].name')">
                <customer-object
                  :name="$lodash.get(relatedJobData, 'job_contacts[0].name')"
                  :entity-id="$lodash.get(relatedJobData, 'job_contacts[0].id')"
                  :open-new="true"
                />
              </template>
              <template v-else> N/A </template>
            </div>
          </template>
        </b-card>

        <b-card v-if="!creating">
          <template #header>
            <b>Reminder</b>
            Info
          </template>

          <div>
            <b>Resolved?</b>
            {{ crud.data.resolved_at ? "Yes" : "No" }}
          </div>

          <div class="mt-2">
            <b>Important?</b>
            {{ crud.data.important ? "Yes" : "No" }}
          </div>

          <div v-if="$lodash.has(crud, 'data.creator.name')" class="mt-2">
            <b>Created By </b>
            <user-object
              :name="crud.data.creator.name"
              :entity-id="crud.data.creator.id"
              :open-new="true"
            />
          </div>

          <div class="mt-2">
            <b>Created</b>
            {{ formatLocalDate(crud.data.created_at) }}
          </div>

          <div class="mt-2">
            <b>Updated</b>
            {{ formatLocalDate(crud.data.updated_at) }}
          </div>

          <div v-if="crud.data.resolved_at !== null" class="mt-2">
            <b>Resolved</b>
            {{ formatLocalDate(crud.data.resolved_at) }}
            <template v-if="crud.data.resolvedBy && crud.data.resolvedBy.id">
              by
              <user-object
                :name="crud.data.resolvedBy.name"
                :entity-id="crud.data.resolvedBy.id"
                :open-new="true"
              />
            </template>
          </div>
        </b-card>
      </div>
    </div>

    <b-card-footer class="mb-5">
      <div class="row">
        <div v-if="!creating" class="col">
          <button
            class="btn btn-success"
            :disabled="form.notReady() || crud.data.resolved_at !== null"
            @click.prevent="resolve"
          >
            <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
            <font-awesome-icon v-else :icon="['fal', 'thumbs-up']" />
            {{ crud.data.resolved_at === null ? "Resolve" : "Resolved" }}
          </button>
        </div>
        <div class="col text-right">
          <button class="btn btn-primary" :disabled="form.notReady()">
            <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
            <font-awesome-icon v-else :icon="['fal', 'check']" />
            {{ creating ? "Create" : "Save" }}
          </button>
        </div>
      </div>
    </b-card-footer>
  </b-form>
</template>

<script type="text/babel">
import format from "../../mixins/format";
import EventBus from "../../imports/event-bus";

const actions = ["Call Back", "Task", "Renewal", "Misc"];

export default {
  mixins: [format],

  props: ["inModal", "jobId"],

  data() {
    return {
      actions,

      crud: null,
      form: new window.AppForm({
        user: "",
        user_id: "",
        action: "",
        resolved: 0,
        due_date: "",
        notes: "",
        job_id: "",
        related_to_job: true,
        important: 0,
      }),
    };
  },

  computed: {
    currentJobId() {
      if (this.crud.data.job_id) {
        return this.crud.data.job_id;
      }

      if (this.jobId) {
        return this.jobId;
      }

      return null;
    },

    creating() {
      if (this.$route.name === "reminders.create") {
        return true;
      }

      return this.inModal && this.inModal === "create";
    },

    relatedJobData() {
      if (this.$route.name === "jobs.edit") {
        return this.$store.state.jobs.currentJob;
      }

      if (this.crud.data.job) {
        return this.crud.data.job;
      }

      return null;
    },
  },

  created() {
    this.init();
  },

  methods: {
    init() {
      this.crud = new window.AppCrud("reminder");

      this.form.reset();
      this.relatedToJob = true;

      // Initialise form data for the reminder, if not creating
      if (!this.creating) {
        this.crud.initFormData(this.form).then(() => {
          this.form.resolved = this.form.resolved === 1;
          this.form.important = this.form.important === 1;

          // Browser Title
          if (this.$lodash.get(this.crud, "data.job_id")) {
            // noinspection JSUnresolvedFunction
            this.$root.setTitle(`Reminder for #${this.$lodash.get(this.crud, "data.job_id")}J`);
          }
        });
      }
    },

    onSubmit() {
      const action = this.inModal;

      if (this.jobId) {
        this.form.job_id = this.jobId;
      }

      this.crud.submit(this.form, action).then((response) => {
        if (this.inModal === "create") {
          this.init();

          this.$root.$emit("bv::hide::modal", "new-reminder");
          window.toastr.success("Reminder created!", "Success");
          EventBus.$emit("reminder-updated");
        } else if (this.crud.getAction() === "create") {
          window.toastr.success("Reminder created!", "Success");

          this.$router.push(
            {
              name: "reminders.edit",
              params: { id: response.reminder.id },
            },
            () => {
              this.init();
            }
          );
        } else {
          this.init();
          window.toastr.success("Reminder updated!", "Success");
        }
      });
    },

    updateAssignee(user) {
      this.form.user = user;
      this.form.user_id = user.id;

      this.form.errors.clear("user_id");
    },

    resolve() {
      window
        .sweetAlert({
          title: "Confirm to resolve this reminder",
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "No",
            confirm: "Yes!",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.form.startProcessing();

            this.$http.put(`/reminders/${this.crud.data.id}/resolve`).then((response) => {
              if (response.status === 200) {
                window.toastr.success("Reminder resolved!", "Success");
                this.init();
              } else {
                window.toastr.warning(
                  "Something went wrong when trying to resolve this reminder!",
                  "Warning"
                );
              }

              this.form.finishProcessing();
            });
          }
        });
    },
  },
};
</script>
